import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle2, XCircle, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';

const API = process.env.REACT_APP_BACKEND_URL;

const VerifyEmail = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('verifying'); // verifying | success | error
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    const token = searchParams.get('token');
    if (!token) {
      setStatus('error');
      setErrorMessage('No verification token found in the link.');
      return;
    }

    const verify = async () => {
      try {
        const res = await fetch(`${API}/api/auth/verify-email?token=${encodeURIComponent(token)}`);
        const data = await res.json();
        if (res.ok && data.verified) {
          setStatus('success');
        } else {
          setStatus('error');
          setErrorMessage(data.detail || 'Verification failed. The link may be expired or already used.');
        }
      } catch {
        setStatus('error');
        setErrorMessage('Something went wrong. Please try again.');
      }
    };

    verify();
  }, [searchParams]);

  return (
    <div data-testid="verify-email-page" className="min-h-screen bg-[#F7F5F2] dark:bg-background flex items-center justify-center px-6">
      <div className="w-full max-w-md text-center">
        <div className="flex items-center gap-2.5 justify-center mb-12 cursor-pointer" onClick={() => navigate('/')}>
          <img src="/lumera-logo.png" alt="Lumera" className="w-10 h-10 rounded-full" />
          <span className="text-xl font-serif font-semibold text-foreground">Lumera</span>
        </div>

        <div className="bg-white dark:bg-card rounded-2xl shadow-sm border border-stone-100 dark:border-border p-10">
          {status === 'verifying' && (
            <>
              <Loader2 className="w-12 h-12 text-[#B49A88] animate-spin mx-auto mb-6" />
              <h1 className="text-2xl font-serif font-bold text-foreground mb-2">Verifying your email...</h1>
              <p className="text-muted-foreground font-sans">Just a moment while we confirm your email address.</p>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="w-16 h-16 bg-green-50 dark:bg-green-900/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle2 className="w-8 h-8 text-green-600" />
              </div>
              <h1 data-testid="verify-success-title" className="text-2xl font-serif font-bold text-foreground mb-2">Email verified</h1>
              <p className="text-muted-foreground font-sans mb-8">Your Lumera account is ready. Start creating something beautiful.</p>
              <Button
                data-testid="verify-go-to-dashboard"
                onClick={() => navigate('/dashboard')}
                className="bg-[#B49A88] text-white hover:bg-[#B49A88]/85 shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all duration-300 font-sans font-semibold px-8 py-5 rounded-full text-base"
              >
                Go to Dashboard
              </Button>
            </>
          )}

          {status === 'error' && (
            <>
              <div className="w-16 h-16 bg-red-50 dark:bg-red-900/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <XCircle className="w-8 h-8 text-red-500" />
              </div>
              <h1 data-testid="verify-error-title" className="text-2xl font-serif font-bold text-foreground mb-2">Verification failed</h1>
              <p className="text-muted-foreground font-sans mb-8">{errorMessage}</p>
              <div className="flex flex-col gap-3">
                <Button
                  data-testid="verify-try-login"
                  onClick={() => navigate('/login')}
                  className="bg-[#B49A88] text-white hover:bg-[#B49A88]/85 shadow-lg transition-all duration-300 font-sans font-semibold px-8 py-5 rounded-full text-base"
                >
                  Go to Login
                </Button>
                <button
                  onClick={() => navigate('/register')}
                  className="text-sm text-[#B49A88] hover:underline font-sans"
                >
                  Create a new account
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default VerifyEmail;
